import { cn } from "@/lib/utils";
import PropTypes from "prop-types";
import ProviderAvatar from "./ProviderAvatar";

/**
 * MessageAvatar component - Renders the avatar for a message
 * Replaces complex absolute positioning logic with simpler grid-based approach
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isUser - Whether this is a user message
 * @param {Object} props.message - The message object
 * @param {Object} props.previousMessage - Previous message for context
 * @param {boolean} props.isStreaming - Whether the message is streaming
 * @param {boolean} props.hasContent - Whether the message has displayable content
 * @param {boolean} props.hasToolCalls - Whether the message has tool calls
 * @param {boolean} props.hasCompletedToolCalls - Whether tool calls are completed
 * @param {boolean} props.isMobile - Whether viewing on mobile
 * @param {boolean} props.isCanvasOpen - Whether canvas is open
 * @param {string} props.className - Additional CSS classes
 */
const MessageAvatar = ({
  isUser,
  message,
  previousMessage = null,
  isStreaming = false,
  hasContent = false,
  hasToolCalls = false,
  hasCompletedToolCalls = false,
  isMobile = false,
  isCanvasOpen = false,
  className = "",
}) => {
  // Don't render avatar for user messages
  if (isUser) {
    return null;
  }

  // Check if this is a reasoning message that's been initialized
  const isReasoningInitialized =
    message?.metadata?.isReasoningResponse === true ||
    message?.metadata?.inReasoningBlock === true;

  // Check if this is a deep research message that's been initialized
  const isDeepResearchInitialized =
    message?.metadata?.isDeepResearch === true ||
    message?.metadata?.isDeepResearchInProgress === true;

  // Don't render if no content and no completed tool calls and not a reasoning/deep research message
  if (
    !(
      (hasContent && message?.content?.trim().length > 0) ||
      (hasToolCalls && hasCompletedToolCalls) ||
      isReasoningInitialized || // Show avatar for initialized reasoning messages even with empty content
      isDeepResearchInitialized // Show avatar for initialized deep research messages even with empty content
    )
  ) {
    return null;
  }

  // Hide if canvas is open (to avoid layout issues)
  if (isCanvasOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        // Grid positioning - avatar takes auto width, aligns at top
        "flex-shrink-0",
        className
      )}
    >
      <ProviderAvatar
        message={message}
        previousMessage={previousMessage}
        isStreaming={isStreaming}
        hasContent={hasContent}
      />
    </div>
  );
};

MessageAvatar.propTypes = {
  isUser: PropTypes.bool.isRequired,
  message: PropTypes.object.isRequired,
  previousMessage: PropTypes.object,
  isStreaming: PropTypes.bool,
  hasContent: PropTypes.bool,
  hasToolCalls: PropTypes.bool,
  hasCompletedToolCalls: PropTypes.bool,
  isMobile: PropTypes.bool,
  isCanvasOpen: PropTypes.bool,
  className: PropTypes.string,
};

export default MessageAvatar;
